/**
 * A freehand signature pad for the e-signature ceremony.
 *
 * The drawing is DECORATION with a job, and the design follows from that. The
 * cryptographic signature is complete without it — the backend paints it into
 * the visible stamp when present and signs identically when absent — so:
 *
 *  * **Drawing is optional.** The pad never gates the Sign button. Requiring a
 *    scribble would add a hurdle the law does not ask for, and a hurried
 *    squiggle drawn to satisfy a required field is worse evidence than none.
 *  * **Export is PNG, never JPEG.** The canvas is transparent and the
 *    transparency is the payload: the backend splits it into a colour image
 *    plus an `/SMask` plane. A JPEG has no alpha channel — the cleared canvas
 *    would arrive as solid black and the whole drawing would read as ink.
 *  * **Ink is navy, matching the stamp** the backend draws around it, so the
 *    printed page reads as one mark rather than a picture pasted on a card.
 *
 * Pointer Events rather than mouse+touch pairs: one API covers mouse, finger
 * and stylus, and `setPointerCapture` keeps a stroke alive when it wanders off
 * the canvas edge mid-curve — losing capture there is how signatures get
 * truncated at the exact moment someone writes with feeling.
 */
import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { dataUrlToBase64 } from './capture';

/** Same navy as the backend stamp's card and seal (#17225F ≈ rgb 18,34,120). */
const INK = '#122278';
const STROKE_WIDTH = 2.4;

export interface SignaturePadHandle {
  /**
   * The drawing as raw base64 PNG, or `null` when nothing has been drawn.
   * Read at submit time — the canvas is the single source of truth, so there
   * is no per-stroke serialisation to drift out of sync with what is visible.
   */
  toPngBase64(): string | null;
}

interface SignaturePadProps {
  disabled?: boolean;
  /**
   * Fires when the pad transitions between empty and drawn. The parent uses it
   * for labelling only — never to gate submission.
   */
  onInkChange?: (hasInk: boolean) => void;
}

export const SignaturePad = forwardRef<SignaturePadHandle, SignaturePadProps>(
  function SignaturePad({ disabled = false, onInkChange }, ref) {
    const { t } = useTranslation();
    const canvasRef = useRef<HTMLCanvasElement | null>(null);
    const drawingRef = useRef(false);
    const [hasInk, setHasInk] = useState(false);
    // Mirrors `hasInk` for the transition check in `setInk`. A ref rather than
    // reading state inside the updater: StrictMode double-invokes updaters, so
    // a side effect (the onInkChange call) placed there would fire twice.
    const hasInkRef = useRef(false);

    // Size the backing store to CSS pixels × devicePixelRatio once per mount.
    // Resizing a canvas CLEARS it, so this deliberately does not track window
    // resizes — wiping someone's signature because they rotated their phone
    // would be worse than a slightly soft stroke.
    useEffect(() => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      const rect = canvas.getBoundingClientRect();
      const dpr = window.devicePixelRatio || 1;
      canvas.width = Math.max(1, Math.round(rect.width * dpr));
      canvas.height = Math.max(1, Math.round(rect.height * dpr));
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.scale(dpr, dpr);
        ctx.strokeStyle = INK;
        ctx.lineWidth = STROKE_WIDTH;
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
      }
    }, []);

    const setInk = useCallback(
      (v: boolean) => {
        if (hasInkRef.current === v) return;
        hasInkRef.current = v;
        setHasInk(v);
        onInkChange?.(v);
      },
      [onInkChange],
    );

    const point = (canvas: HTMLCanvasElement, e: React.PointerEvent) => {
      const rect = canvas.getBoundingClientRect();
      return { x: e.clientX - rect.left, y: e.clientY - rect.top };
    };

    const onPointerDown = useCallback(
      (e: React.PointerEvent<HTMLCanvasElement>) => {
        if (disabled) return;
        const canvas = canvasRef.current;
        const ctx = canvas?.getContext('2d');
        if (!canvas || !ctx) return;
        canvas.setPointerCapture(e.pointerId);
        drawingRef.current = true;
        const { x, y } = point(canvas, e);
        ctx.beginPath();
        ctx.moveTo(x, y);
        // A tap with no movement is still a mark — a dot is how some people
        // dot an i, and swallowing it makes the pad feel broken.
        ctx.lineTo(x + 0.01, y);
        ctx.stroke();
        setInk(true);
      },
      [disabled, setInk],
    );

    const onPointerMove = useCallback((e: React.PointerEvent<HTMLCanvasElement>) => {
      if (!drawingRef.current) return;
      const canvas = canvasRef.current;
      const ctx = canvas?.getContext('2d');
      if (!canvas || !ctx) return;
      const { x, y } = point(canvas, e);
      ctx.lineTo(x, y);
      ctx.stroke();
    }, []);

    const endStroke = useCallback(() => {
      drawingRef.current = false;
    }, []);

    const clear = useCallback(() => {
      const canvas = canvasRef.current;
      const ctx = canvas?.getContext('2d');
      if (!canvas || !ctx) return;
      // Clear in DEVICE pixels, unaffected by the dpr transform on the context.
      ctx.save();
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.restore();
      setInk(false);
    }, [setInk]);

    useImperativeHandle(
      ref,
      () => ({
        toPngBase64() {
          const canvas = canvasRef.current;
          if (!canvas || !hasInk) return null;
          try {
            return dataUrlToBase64(canvas.toDataURL('image/png'));
          } catch {
            // A tainted or unreadable canvas must degrade to "no drawing",
            // exactly as the backend degrades a drawing it cannot decode: the
            // signature itself is never held hostage by its decoration.
            return null;
          }
        },
      }),
      [hasInk],
    );

    return (
      <div className="sign-pad" data-testid="signature-pad">
        <div className="sign-pad-head">
          <span className="details-label">
            {t('flow.sign.padLabel', { defaultValue: 'Draw your signature (optional)' })}
          </span>
          <button
            type="button"
            className="sign-pad-clear"
            disabled={disabled || !hasInk}
            data-testid="signature-pad-clear"
            data-track="clear_signature"
            onClick={clear}
          >
            {t('flow.sign.padClear', { defaultValue: 'Clear' })}
          </button>
        </div>
        <canvas
          ref={canvasRef}
          className="sign-pad-canvas"
          data-testid="signature-pad-canvas"
          aria-label={t('flow.sign.padLabel', {
            defaultValue: 'Draw your signature (optional)',
          })}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={endStroke}
          onPointerCancel={endStroke}
        />
        <p className="sign-pad-hint">
          {t('flow.sign.padHint', {
            defaultValue:
              'Shown on the signed document next to our digital seal. Signing works with or without it.',
          })}
        </p>
      </div>
    );
  },
);
